import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Button,
  Form,
  Input,
  ModalFooter,
  SelectSearch,
} from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import type { DamRaisingMethod } from "../../api/structures/structures.types";
import {
  createAdminStructureRaisingHistorySchema,
  type AdminStructureRaisingHistoryForm as RaisingHistoryFormValues,
} from "../../schemas";

const DAM_RAISING_METHODS: DamRaisingMethod[] = [
  "downstream",
  "upstream",
  "centerLine",
];

const RaisingHistoryDateField = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  errorMessage,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name: string;
  errorMessage?: string;
}) => (
  <label className="group flex min-w-0 w-full flex-col gap-1">
    <span className="text-label group-aria-invalid:text-error">{label}</span>
    <input
      type="date"
      aria-label={label}
      aria-invalid={Boolean(errorMessage) || undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      name={name}
      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
    />
    {errorMessage ? (
      <span className="text-error text-sm">{errorMessage}</span>
    ) : null}
  </label>
);

export type AdminStructureRaisingHistoryFormProps = {
  defaultValues: RaisingHistoryFormValues;
  isSubmitting: boolean;
  onSubmit: (values: RaisingHistoryFormValues) => Promise<void>;
  onCancel: () => void;
};

export const AdminStructureRaisingHistoryForm = ({
  defaultValues,
  isSubmitting,
  onSubmit,
  onCancel,
}: AdminStructureRaisingHistoryFormProps) => {
  const { translate } = useLocalization();

  const schema = useMemo(
    () => createAdminStructureRaisingHistorySchema(translate),
    [translate],
  );

  const form = useForm<RaisingHistoryFormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    shouldFocusError: true,
    defaultValues,
  });

  const methodOptions = useMemo(
    () =>
      DAM_RAISING_METHODS.map((value) => ({
        value,
        label: translate(`admin.structures.raisingHistory.method.${value}`),
      })),
    [translate],
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit, () => {
          void form.trigger(undefined, { shouldFocus: true });
        })}
      >
        <div className="flex flex-col gap-4 px-8 py-4">
          <Input
            control={form.control}
            name="executiveProjectDesigner"
            label={translate(
              "admin.structures.raisingHistory.label.executiveProjectDesigner",
            )}
            type="text"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="stage"
              label={translate("admin.structures.raisingHistory.label.stage")}
              type="text"
            />
            <SelectSearch
              control={form.control}
              name="method"
              label={translate("admin.structures.raisingHistory.label.method")}
              options={methodOptions}
              placeholder={translate(
                "admin.structures.raisingHistory.filter.placeholder.method",
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="elevation"
              label={translate("admin.structures.raisingHistory.label.elevation")}
              type="text"
              inputMode="decimal"
            />
            <RaisingHistoryDateField
              label={translate("admin.structures.raisingHistory.label.occurredOn")}
              value={form.watch("occurredOn")}
              onChange={(value) =>
                form.setValue("occurredOn", value, { shouldValidate: true })
              }
              onBlur={() => void form.trigger("occurredOn")}
              name="occurredOn"
              errorMessage={form.formState.errors.occurredOn?.message}
            />
          </div>
        </div>
        <ModalFooter
          footer={
            <Button type="submit" disabled={isSubmitting}>
              {translate("admin.structures.modal.button.save")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("admin.structures.modal.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
